import { useCallback, useRef, useState } from 'react';
import type { DrillCone } from '@4cm/core/drills/types';
import { EquipmentSvg } from './EquipmentSvg';
import { defaultRotationFor, type EquipmentVariant } from './equipmentTypes';
import { pitchViewBox, pitchBackgroundRect, drillNormToPitchSvg, pitchSvgToDrillNorm, type PitchView, type CustomPitch } from './pitchArea';
import type { PitchSpec } from './pitchSpecs';

interface EquipmentOverlay2DProps {
  cones: DrillCone[];
  onChange: (next: DrillCone[]) => void;
  /** Currently armed tool from the tray. null = idle (overlay is
   *  click-through to the board beneath). */
  activeTool: EquipmentVariant | null;
  /** Called when the user finishes placing / cancels — tray-controlled
   *  so the tool can stay armed for multi-place. */
  onPlaced?: () => void;
  /** Active pitch spec — drives viewBox + coord conversion. */
  spec: PitchSpec;
  view: PitchView;
  custom?: CustomPitch | null;
}

/**
 * Transparent SVG overlay that sits ON TOP of Josh's TacticsBoard2D.
 *
 *   - When `activeTool` is null, pointer-events: none. Clicks pass
 *     through to the board so the coach can drag players + arrows.
 *   - When a tool is armed, the overlay captures pointer events:
 *     clicking on the pitch drops a cone of that variant at the
 *     clicked normalised position.
 *   - Existing cones in the overlay are draggable + selectable.
 *
 * The overlay assumes the parent container is set up with matching
 * aspect ratio so its viewBox aligns with the board's pitch. Width
 * fills 100%; height is computed from the same aspect.
 */
export function EquipmentOverlay2D({
  cones,
  onChange,
  activeTool,
  onPlaced,
  spec,
  view,
  custom,
}: EquipmentOverlay2DProps): React.JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  // 1 yard ≈ 0.9144 m. Pass this to EquipmentSvg so cone sizes feel
  // right in Josh's metre-scaled landscape viewBox.
  const yd = 0.9144;

  // Convert pointer event to normalised [0..1] drill coords via Josh's
  // landscape SVG coord system.
  const eventToNorm = useCallback((evt: React.PointerEvent<SVGSVGElement>): [number, number] | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const local = pt.matrixTransform(ctm.inverse());
    return pitchSvgToDrillNorm(local.x, local.y, spec, view, custom);
  }, [spec, view, custom]);

  // Click on empty pitch with an armed tool → place a new cone.
  const handleBackgroundDown = useCallback((evt: React.PointerEvent<SVGSVGElement>) => {
    if (!activeTool) return;
    // Only place when the click hits the background <rect>, not an existing cone.
    if ((evt.target as Element).tagName !== 'rect' || (evt.target as Element).getAttribute('data-bg') !== '1') return;
    const norm = eventToNorm(evt);
    if (!norm) return;
    const id = `eq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const cone: DrillCone = {
      id,
      position: norm,
      variant: activeTool,
      rotation: defaultRotationFor(activeTool),
    };
    onChange([...cones, cone]);
    setSelectedId(id);
    onPlaced?.();
  }, [activeTool, cones, eventToNorm, onChange, onPlaced]);

  // Pointer down on an existing cone → start dragging (always available,
  // regardless of armed tool).
  const handleConeDown = useCallback((id: string) => (evt: React.PointerEvent<SVGGElement>) => {
    evt.stopPropagation();
    setSelectedId(id);
    setDragId(id);
    (evt.currentTarget as Element).setPointerCapture?.(evt.pointerId);
  }, []);

  const handlePointerMove = useCallback((evt: React.PointerEvent<SVGSVGElement>) => {
    if (!dragId) return;
    const norm = eventToNorm(evt);
    if (!norm) return;
    onChange(cones.map((c) => (c.id === dragId ? { ...c, position: norm } : c)));
  }, [dragId, cones, eventToNorm, onChange]);

  const handlePointerUp = useCallback(() => {
    setDragId(null);
  }, []);

  // Delete / Backspace removes the selected cone.
  // (Wired on the parent so it works even when overlay isn't focused.)

  // The SVG always listens so placed cones can be dragged in Select
  // mode. The background <rect> only activates when an equipment tool
  // is armed — otherwise empty-pitch clicks pass through. Use 'all'
  // because the rect has fill="transparent" and SVG's default
  // visiblePainted treats that as not-painted.
  const bgPointerEvents = activeTool !== null ? 'all' : 'none';

  return (
    <svg
      ref={svgRef}
      viewBox={pitchViewBox(spec, view, custom)}
      preserveAspectRatio="xMidYMid meet"
      onPointerDown={handleBackgroundDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        // SVG itself is click-through when no tool is armed and no
        // cone is being dragged. Cone groups override with their own
        // pointer-events: auto.
        pointerEvents: activeTool !== null || dragId !== null ? 'auto' : 'none',
      }}
      aria-label="Drill equipment overlay"
    >
      {/* Invisible background so taps register when armed. */}
      <rect
        data-bg="1"
        {...pitchBackgroundRect(spec, view, custom)}
        fill="transparent"
        style={{
          cursor: activeTool ? 'crosshair' : 'default',
          pointerEvents: bgPointerEvents,
        }}
      />
      {cones.map((c) => {
        const { cx, cy } = drillNormToPitchSvg(c.position, spec, view, custom);
        return (
          <EquipmentSvg
            key={c.id}
            cone={c}
            cx={cx}
            cy={cy}
            pxPerYd={yd}
            selected={selectedId === c.id}
            onPointerDown={handleConeDown(c.id)}
          />
        );
      })}
    </svg>
  );
}
